import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Custom hook to scroll to top on route change
 * @param {boolean} immediate - Whether to scroll immediately on mount (default: true)
 * @param {number} delay - Delay in milliseconds before scrolling (default: 0)
 * @param {boolean} force - Force scroll to top even if it's the same pathname (default: false)
 */
export function useScrollToTop(immediate = true, delay = 0, force = false) {
  const location = useLocation();
  const previousPathname = useRef(location.pathname);

  useEffect(() => {
    const scrollToTop = () => {
      // Force scroll to top immediately to prevent any scroll restoration
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: immediate ? "auto" : "smooth",
      });

      // Also ensure document element is scrolled to top
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }

      // And body element as well for maximum compatibility
      if (document.body) {
        document.body.scrollTop = 0;
      }
    };

    // Check if pathname has changed or if force is enabled
    const hasPathnameChanged = previousPathname.current !== location.pathname;

    if (hasPathnameChanged || force) {
      // Update the previous pathname
      previousPathname.current = location.pathname;

      if (delay > 0) {
        const timeoutId = setTimeout(scrollToTop, delay);
        return () => clearTimeout(timeoutId);
      } else {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          scrollToTop();
        });
      }
    }
  }, [
    location.pathname,
    location.search,
    location.hash,
    immediate,
    delay,
    force,
  ]);

  // Also handle popstate events (back/forward navigation)
  useEffect(() => {
    const handlePopState = () => {
      // Force scroll to top on back/forward navigation
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });

        if (document.documentElement) {
          document.documentElement.scrollTop = 0;
        }

        if (document.body) {
          document.body.scrollTop = 0;
        }
      });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);
}

export default useScrollToTop;
